import { getMedianPrice } from "../../../helpers/gift-list";
import { emptyGiftItem } from "../../../helpers/gift-list/emptyGiftItem";
import { translate } from "../../../helpers/translate";
import { GiftGroup, GiftItem, PosEntityGiftGroups } from "../../../types";
import { updateGiftGroupItemsResult } from "../../actions/gifts-actions";
import { store } from "../../configureStore";
import { MultiPosGiftGroupsState } from "./multiPosGiftGroupsReducer";
import { v4 as uuidv4 } from "uuid";
import Strings from "../../../i18n/strings/giftList";

const t = translate("giftList");
export const isReferencePos = (
  state: MultiPosGiftGroupsState,
  posId: string
) => {
  const referencePosId = state.posEntities[0].id;
  return posId === referencePosId;
};

export const dispatchUpdateGiftGroupItemsResult = (
  item: GiftItem,
  success: boolean
) => {
  setTimeout(
    () =>
      store.dispatch(
        updateGiftGroupItemsResult({
          success,
          item,
          category_id: item.category_id,
        })
      ),
    0
  );
};

export const createPlaceholderItem = (price: number): GiftItem => ({
  ...emptyGiftItem(1),
  name: "Placeholder Item",
  description: "This item needs to be configured for this POS",
  price,
  is_placeholder: 1,
});

export const createPlaceholderGroup = ({
  id = uuidv4(),
  name,
  ar_name,
  price = 0,
  currency = {
    en: "",
    ar: "",
  },
  itemPrice = price,
}: {
  id?: string;
  name?: string;
  ar_name?: string;
  price?: number;
  currency?: {
    en: string;
    ar: string;
  };
  itemPrice?: number;
}): GiftGroup => ({
  id,
  name: name ?? `Gift - X ${currency.en}`,
  ar_name: ar_name ?? `هدية - X ${currency.ar}`,
  is_renamed: 0,
  is_ar_renamed: 0,
  items: [{ ...createPlaceholderItem(itemPrice), show_price: 1 }],
  price,
  is_placeholder: 1,
});

export const createUpdatedGroupData = (
  groupId: string,
  items: GiftItem[],
  currency: {
    en: string;
    ar: string;
  }
) => {
  const price = getMedianPrice(items);
  return {
    id: groupId,
    is_renamed: 0,
    name: `Gift - ${price} ${currency.en}`,
    ar_name: `هدية - ${price} ${currency.ar}`,
    is_ar_renamed: 0,
    items,
    price,
  };
};

export const validateGroupCompatibility = (
  state: MultiPosGiftGroupsState,
  pos_entity_id: string,
  group_id: string,
  updatedItems: GiftItem[]
): { isValid: boolean; errorMessage?: string } => {
  if (isReferencePos(state, pos_entity_id)) {
    return { isValid: true };
  }

  const referencePosId = Object.keys(state.giftGroupsByPosId)[0];
  const referenceGroup =
    state.giftGroupsByPosId[referencePosId].shownGiftGroupsById[group_id];
  const errorMessage = t(Strings.addItemsToFirstPOS, {
    pos_name: state.posEntities[0].name,
  });
  if (!referenceGroup) {
    return {
      isValid: false,
      errorMessage,
    };
  }

  const referenceItems = referenceGroup.items as GiftItem[];
  const isCompatible = updatedItems.every((item, index) => {
    const referenceItem = referenceItems[index];
    return referenceItem && Math.abs(item.price - referenceItem.price) < 0.01;
  });

  if (!isCompatible) {
    return {
      isValid: false,
      errorMessage,
    };
  }

  return { isValid: true };
};

export const updateExistingGroup = (
  group: GiftGroup,
  updatedItems: GiftItem[],
  currency: {
    en: string;
    ar: string;
  }
) => {
  const updatedGroupPrice = getMedianPrice(updatedItems);

  return {
    ...group,
    name: group.is_renamed
      ? group.name
      : `Gift - ${updatedGroupPrice} ${currency.en}`,
    ar_name: group.is_ar_renamed
      ? group.ar_name
      : `هدية - ${updatedGroupPrice} ${currency.ar}`,
    items: updatedItems,
    price: updatedGroupPrice,
  };
};

export const createUpdatedGiftGroupEntries = (
  state: MultiPosGiftGroupsState,
  pos_entity_id: string,
  newGroupId: string,
  item: GiftItem,
  currency: {
    en: string;
    ar: string;
  }
) => {
  const groupName = `Gift - ${Math.round(item.price)} ${currency.en}`;
  const groupArName = `هدية - ${Math.round(item.price)} ${currency.ar}`;

  return Object.entries(state.giftGroupsByPosId).map(([posId, posState]) => {
    const newGroupEntry =
      posId === pos_entity_id
        ? {
          id: newGroupId,
          name: groupName,
          ar_name: groupArName,
          items: [
            {
              ...item,
              order: 1,
              price: item.price,
            },
          ],
          price: Math.round(item.price),
          is_renamed: 0,
        }
        : createPlaceholderGroup({
          name: groupName,
          ar_name: groupArName,
          itemPrice: item.price,
          price: Math.round(item.price),
          currency,
        });

    const updatedById = {
      ...posState.shownGiftGroupsById,
      [newGroupId]: newGroupEntry,
    };

    const updatedIds = Object.keys(updatedById).sort(
      (a, b) => updatedById[a].price - updatedById[b].price
    );

    return [
      posId,
      {
        ...posState,
        shownGiftGroupsById: updatedById,
        shownGiftGroupsIds: updatedIds,
      },
    ];
  });
};

export const isPriceOutsideRange = (group: GiftGroup, itemPrice: number) => {
  if (!group.items.length) return false;

  const firstItem = group.items[0] as GiftItem;
  const price = firstItem.price;
  const diff = price * 0.1;
  return itemPrice > price + diff || itemPrice < price - diff;
};

export const isAllGiftGroupsEmpty = (posPayload: PosEntityGiftGroups[]) =>
  posPayload.every((pos) =>
    !pos.gift_groups || pos.gift_groups.length === 0 ||
    pos.gift_groups.every(
      (group) => !group.items || group.items.length === 0
    )
  );

export const syncPlaceholderItem = (
  existingItems: GiftItem[],
  sourceItem: GiftItem
): GiftItem[] => {
  const updatedItems = [...existingItems];
  const targetOrder =
    sourceItem.order ?? updatedItems.findIndex((item) => item.id === "") + 1;

  const replacementIndex = updatedItems.findIndex(
    (item) => item.order === targetOrder || item.id === ""
  );

  if (replacementIndex !== -1) {
    updatedItems[replacementIndex] = {
      ...emptyGiftItem(targetOrder),
      price: sourceItem.price,
      show_price: 1,
      is_placeholder: 1,
      order: targetOrder,
    };
  } else {
    updatedItems.push({
      ...emptyGiftItem(updatedItems.length + 1),
      price: sourceItem.price,
      show_price: 1,
      is_placeholder: 1,
      order: updatedItems.length + 1,
    });
  }

  return updatedItems;
};
